/**
 * Annotation Store — external store for annotation data that allows
 * individual components to subscribe to specific annotations without
 * React context. This avoids fiber-tree walking for unchanged items.
 *
 * When an annotation is created/deleted, only the ID list changes.
 * Individual annotation components subscribe to their own data via
 * useSyncExternalStore, so React only visits the changed fibers.
 */

import { useSyncExternalStore, useCallback, useRef } from 'react';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import type { AnnotationsWithIndices } from '../../modules/renderer/RendererLeftPanel/Annotations/utils/resolveDropPosition';

type Listener = () => void;

interface AnnotationStoreState {
  /** Ordered list of top-level identifiers (annotations + groups) */
  orderedIds: string[];
  /** All annotations and groups by identifier, with index */
  byIdentifier: Record<string, AnnotationsWithIndices[0]>;
  /** The full indexed annotations array (for drag-drop calculations) */
  indexedAnnotations: AnnotationsWithIndices;
}

const EMPTY_STATE: AnnotationStoreState = {
  orderedIds: [],
  byIdentifier: {},
  indexedAnnotations: [],
};

class AnnotationStore {
  private state: AnnotationStoreState = EMPTY_STATE;
  private listeners = new Set<Listener>();

  getState() {
    return this.state;
  }

  /** Update store from new indexed annotations. Only notifies if data actually changed. */
  update(indexedAnnotations: AnnotationsWithIndices) {
    const orderedIds = indexedAnnotations.map((a) => a.identifier);
    const byIdentifier: Record<string, AnnotationsWithIndices[0]> = {};
    // Recursive: nested room folders put annotations at depth 2 — every level
    // must be addressable by identifier (drag-drop lookups, submenus).
    const collect = (items: readonly AnnotationsWithIndices[0][]): void => {
      for (const a of items) {
        byIdentifier[a.identifier] = a;
        if (isAnnotationContextGroup(a)) collect(a.annotations as AnnotationsWithIndices[0][]);
      }
    };
    collect(indexedAnnotations);

    // Check if orderedIds actually changed
    const prev = this.state;
    const idsChanged =
      orderedIds.length !== prev.orderedIds.length ||
      orderedIds.some((id, i) => id !== prev.orderedIds[i]);

    // Check if any annotation data changed
    let dataChanged = idsChanged;
    if (!dataChanged) {
      for (const id of orderedIds) {
        if (byIdentifier[id] !== prev.byIdentifier[id]) {
          dataChanged = true;
          break;
        }
      }
    }

    if (!dataChanged) return;

    this.state = { orderedIds, byIdentifier, indexedAnnotations };
    this.notify();
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// Singleton store instance
export const annotationStore = new AnnotationStore();

// ---------------------------------------------------------------------------
// Hooks — surgical subscriptions that only trigger re-renders when
// the specific data the component cares about actually changes.
// ---------------------------------------------------------------------------

/** Subscribe to the ordered list of top-level annotation IDs.
 *  Only re-renders when IDs are added, removed, or reordered. */
export function useAnnotationIds(): string[] {
  const subscribe = useCallback((cb: Listener) => annotationStore.subscribe(cb), []);
  const prevRef = useRef<string[]>([]);

  return useSyncExternalStore(subscribe, () => {
    const ids = annotationStore.getState().orderedIds;
    // Return same reference if unchanged to prevent parent re-renders
    if (
      ids.length === prevRef.current.length &&
      ids.every((id, i) => id === prevRef.current[i])
    ) {
      return prevRef.current;
    }
    prevRef.current = ids;
    return ids;
  });
}

/** Subscribe to a single annotation/group by identifier.
 *  Only re-renders when THIS annotation's data changes. */
export function useAnnotationById(identifier: string): AnnotationsWithIndices[0] | undefined {
  const subscribe = useCallback((cb: Listener) => annotationStore.subscribe(cb), []);
  return useSyncExternalStore(subscribe, () => {
    return annotationStore.getState().byIdentifier[identifier];
  });
}

/** Subscribe to the full indexed annotations array.
 *  Use sparingly — only for drag-drop calculations that need the full list. */
export function useIndexedAnnotations(): AnnotationsWithIndices {
  const subscribe = useCallback((cb: Listener) => annotationStore.subscribe(cb), []);
  return useSyncExternalStore(subscribe, () => {
    return annotationStore.getState().indexedAnnotations;
  });
}

/** Get the annotation-by-identifier lookup map (non-reactive, reads current state). */
export function getAnnotationsByIdentifier(): Record<string, AnnotationsWithIndices[0]> {
  return annotationStore.getState().byIdentifier;
}
